/**
 * Where you stand with all seven departments, and what it costs to move.
 *
 * One rule runs the whole system: **nothing ever moves alone**. Every change is
 * pushed through the rivalry matrix, so a favour done for Banquets is a slight
 * to the Kitchen whether you wanted it to be or not. That is the game. A
 * standing system where you can simply accumulate goodwill everywhere is a
 * progress bar, not a politics.
 *
 * Two decisions worth stating, because both could reasonably have gone the
 * other way:
 *
 *   - The ripple is computed from the delta that was *actually applied*, not
 *     the one that was asked for. Once Banquets is pinned at 100, another
 *     favour for Banquets moves nothing and therefore costs nothing. The
 *     alternative — ripple the intended delta regardless — turns a maxed
 *     department into a free weapon for grinding a rival into the floor.
 *   - The matrix is read one hop only. Gaining with Banquets costs the Kitchen,
 *     and does *not* then apply the Kitchen's own rivalries to everyone else.
 *     Chained ripples oscillate, are impossible to reason about at the design
 *     level, and would make every single BEO move all seven numbers.
 *
 * Pure. No storage, no UI, no knowledge of what a BEO is.
 */

export const FLOOR = -100;
export const CEILING = 100;

/** The words the debug panel and the NPCs use for a number. */
export const TIERS = Object.freeze([
  { at: -70, label: 'Hated', tone: 'bad' },
  { at: -35, label: 'Resented', tone: 'bad' },
  { at: -10, label: 'Wary', tone: 'warn' },
  { at: 10, label: 'Neutral', tone: 'idle' },
  { at: 35, label: 'Friendly', tone: 'good' },
  { at: 70, label: 'Trusted', tone: 'good' },
  { at: Infinity, label: 'Family', tone: 'good' },
]);

export function tierFor(value) {
  return TIERS.find((tier) => value < tier.at) ?? TIERS[TIERS.length - 1];
}

/**
 * The ladder, which is the only thing standing is allowed to unlock.
 *
 * Four rungs, and everything a department will do for you that is not a price
 * is read off which one you are on. A door is open or it is not. A resolved
 * shortfall is left where it sat, carried half way, or waiting for you when you
 * arrive. There is no coefficient anywhere and there is not going to be one:
 * a rung is a thing a player can hold in their head and plan a shift around,
 * and a number that slides is a thing they can only notice afterwards.
 *
 * The seven `TIERS` above are words for the debug panel and the chatter — they
 * are how a number *reads*. These four are what a number *does*, and they are
 * deliberately fewer, because four states are learnable in a shift and seven
 * are not.
 *
 * Same shape as TIERS: `at` is the value this rung stops at.
 */
export const RUNGS = Object.freeze([
  { id: 'hostile', at: -20, label: 'Hostile' },
  { id: 'cold', at: 25, label: 'Cold' },
  { id: 'warm', at: 55, label: 'Warm' },
  { id: 'trusted', at: Infinity, label: 'Trusted' },
]);

export const RUNG_IDS = Object.freeze(RUNGS.map((rung) => rung.id));

export function rungFor(value) {
  return RUNGS.find((rung) => value < rung.at) ?? RUNGS[RUNGS.length - 1];
}

/** Whether a value is on `wanted` or above it. */
export function onRung(value, wanted) {
  const have = RUNG_IDS.indexOf(rungFor(value).id);
  const need = RUNG_IDS.indexOf(wanted);
  return need === -1 || have >= need;
}

/** The lowest value that stands on a rung, for anything counting the gap. */
export function floorOf(rung) {
  const index = RUNG_IDS.indexOf(rung);
  return index <= 0 ? FLOOR : RUNGS[index - 1].at;
}

/**
 * The value one whole step below where this one sits.
 *
 * A step down, not a subtraction, and not a fixed floor either. Being caught
 * with your hands in somebody's cart should cost the same *thing* whoever you
 * are — the step you were on — rather than the same number, which is nothing to
 * a new hire and everything to somebody who spent a week earning it.
 *
 * One under the bottom of the current step, so you land at the top of the one
 * below and keep whatever you had built inside it. Losing the step is the
 * punishment; losing the week as well would be two.
 */
export function belowRung(value) {
  return floorOf(rungFor(value).id) - 1;
}

export function createStanding({ departments, rivalry, initial = {} }) {
  const ids = departments.map((department) => department.id);
  const values = new Map(ids.map((id) => [id, clamp(initial[id] ?? 0)]));
  const listeners = new Set();

  function clamp(value) {
    return Math.max(FLOOR, Math.min(CEILING, value));
  }

  function move(id, delta) {
    const before = values.get(id);
    const after = clamp(before + delta);
    values.set(id, after);
    return { department: id, from: before, to: after, delta: after - before };
  }

  return {
    ids,
    get(id) {
      return values.get(id) ?? 0;
    },
    /** Every department, in declaration order, with its tier. */
    all() {
      return departments.map((department) => {
        const value = values.get(department.id) ?? 0;
        return { ...department, value, tier: tierFor(value) };
      });
    },

    /**
     * Applies a change and every consequence of it.
     *
     * Returns the full list of movements, primary first, so a caller can show
     * the player exactly what their choice cost — which is the only way the
     * matrix is ever going to be legible from inside the game.
     */
    /*
     * `spared` are left out of this change's ripple altogether. A room's award
     * is two primaries, Banquets' share and the owner's, and without it the
     * owner's share was cancelled by Banquets' ripple onto it: setting the
     * Kitchen's own room well moved the Kitchen by nothing at all (+0.6 of the
     * base direct, -0.6 of it through `RIVALRY.banquets.kitchen`). Doing a
     * department's own room is not doing Banquets a favour at its expense.
     */
    apply(id, delta, { reason = null, noGain = [], spared = [] } = {}) {
      if (!values.has(id)) throw new Error(`no department "${id}"`);
      /*
       * Departments that must not come out of this better off than they went
       * in, whatever the matrix says.
       *
       * There is exactly one situation that needs it and it is not a special
       * case, it is a hole in the shape of the matrix. The matrix says a
       * department's rivals gain when it loses, which is right for almost
       * everything — an event burns, Management is furious, the people who
       * cannot stand Management enjoy it. It is wrong for a *report*, because
       * the department that picked up the phone is one of those rivals. Ask the
       * Front Desk for something below warm and she reports you to Management:
       * Management drops fourteen, and the Front Desk — hers — went up one and
       * a half for the trouble of reporting you. The building was rewarding you
       * for being turned in by the person you were standing in front of.
       *
       * Held to the line rather than punished, because how much a reporter's own
       * opinion should fall is a question about that person and belongs on their
       * row. What must never happen is that it rises, and that is not a taste.
       */
      const held = new Set(noGain.filter((department) => values.has(department)));
      const was = new Map([...held].map((department) => [department, values.get(department)]));

      const primary = move(id, delta);
      const ripples = [];

      // Ripple on what actually landed, and only when something did.
      if (primary.delta !== 0) {
        for (const [other, coefficient] of Object.entries(rivalry[id] ?? {})) {
          if (!values.has(other) || other === id || !coefficient || spared.includes(other)) continue;
          const change = move(other, primary.delta * coefficient);
          if (change.delta !== 0) ripples.push(change);
        }
      }

      // Put back anything a held department gained, from wherever it came.
      for (const [department, before] of was) {
        if (values.get(department) > before) values.set(department, before);
      }
      const settle = (change) => {
        if (!was.has(change.department)) return change;
        const to = values.get(change.department);
        return { ...change, to, delta: to - change.from };
      };

      const event = {
        reason,
        // The primary stays first and stays present even at zero; callers read
        // it as "the thing you actually did".
        changes: [settle(primary), ...ripples.map(settle).filter((change) => change.delta !== 0)],
      };
      for (const listener of listeners) listener(event);
      return event;
    },

    /**
     * Applies a change with **no ripple**.
     *
     * The matrix models "gaining with X costs Y", which is the right shape for
     * a favour done or a room set. It is exactly the wrong shape for damage:
     * an event burning is not you winning, and letting the Kitchen's ruined
     * evening pay out to Banquets through `RIVALRY.kitchen.banquets` would read
     * as the game rewarding you for their disaster.
     *
     * So ghost outcomes, discovered thefts and being reported all land direct.
     * Anything the player *earned* still goes through `apply`.
     */
    applyDirect(entries, { reason = null } = {}) {
      const changes = [];
      for (const [id, delta] of Object.entries(entries)) {
        if (!values.has(id)) continue;
        const change = move(id, delta);
        if (change.delta !== 0) changes.push(change);
      }
      const event = { reason, changes, direct: true };
      for (const listener of listeners) listener(event);
      return event;
    },

    /**
     * Drops a department to a floor, if it is not already below it.
     *
     * Being caught taking is not a subtraction, it is a verdict. A player at
     * +80 with Housekeeping and a player at -10 both end up in the same place,
     * and that is the point — there is no amount of goodwill that survives
     * being seen walking off with their linen.
     */
    floorTo(id, floor, { reason = null } = {}) {
      if (!values.has(id)) return { reason, changes: [], direct: true };
      const before = values.get(id);
      if (before <= floor) return { reason, changes: [], direct: true };
      values.set(id, clamp(floor));
      const event = {
        reason,
        direct: true,
        changes: [{ department: id, from: before, to: clamp(floor), delta: clamp(floor) - before }],
      };
      for (const listener of listeners) listener(event);
      return event;
    },

    /** Applies several primaries at once, each with its own ripple. */
    applyAll(entries, { reason = null, noGain = [], spared = [] } = {}) {
      const changes = [];
      for (const [id, delta] of Object.entries(entries)) {
        // Ripples are applied per primary rather than summed first, so an award
        // that touches two departments cannot silently cancel its own fallout.
        // `noGain` has to be passed to each of them for the same reason: a
        // department held out of the first primary's ripple and not the second
        // is held out of nothing.
        changes.push(...this.apply(id, delta, { reason, noGain, spared }).changes);
      }
      return { reason, changes };
    },

    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    toJSON: () => Object.fromEntries(values),
    load(state) {
      for (const id of ids) values.set(id, clamp(state?.[id] ?? 0));
    },
  };
}
